/**
 * The writes of the case page and the Outlook pane, in one place.
 *
 * "Take" alone is never the primary action. The server takes an unassigned case with the
 * first write on it (a line decision, a quantity, a price, an accept, the customer, a
 * status), so the page sends that write at once and no take in front of it (ease round,
 * 13 Oct 2026: one request less before the first save). Three actions have no such write
 * and still take the case first: "Prepare the reply", "Look again" and "Not this one". A
 * take that fails (a colleague took it in between: the conflict toast says so and the case
 * is read again) stops them.
 *
 * Every save says what happened, where the eye is: the cell or the row changes at once
 * (the hooks write the cached case before the server answers), a short toast follows,
 * with "Undo" where an undo exists, and a failed save says "Not saved" with the reason.
 * An inline quantity shows the saved mark at its cell and no toast: five lines would
 * raise five. A save whose value is the one on the page sends nothing and says "No
 * change".
 *
 * Every write reaches the app database only; nothing is sent.
 */

import { useToastManager } from "@norican/react";
import { useState } from "react";

import { rowNumberOf } from "@/features/case-review/case-action";
import { customerProposalIdFor, lineProposalIdFor } from "@/features/case-review/investigation";
import {
  approvalFailure,
  type CaseWork,
  useAcceptMatched,
  useCaseAssign,
  useCaseCustomer,
  useCaseStatus,
  useCreateAnswerDraft,
  useLineDecision,
  useLineOverride,
  useLinePriceApproval,
  useLineQuantities,
  useLookAgain,
  useMe,
  useRejectProposal,
  workFailureText,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { type QuoteCaseDetail, type QuoteLine } from "@/lib/mock";
import { acceptedText, ACTION_LABELS, NO_ITEM_TOAST, SAVE_TOASTS } from "@/lib/sales-flow";
import { INVESTIGATION_WORDS } from "@/lib/work-labels";

/** The decision on one line: what, and for a pick which item. */
export interface LineDecisionRequestBody {
  kind: "confirm" | "item" | "no_item" | "clear";
  item?: string;
  item_area?: string;
}

/**
 * The detail of a 404 on "Not this one": the case was read again in the meantime and no
 * longer shows the proposal. The page reads the case again; the sentence says why.
 */
export const PROPOSAL_GONE_NOTE = "This proposal is not on the case any more.";

/** A price a person sets on one line (round 4): a decimal string, a currency, an optional note. */
export interface LinePriceRequestBody {
  unit_price: string;
  currency: string;
  price_note?: string;
}

/** What the toast says after a price was set or taken off. */
export const PRICE_SAVED_NOTE = SAVE_TOASTS.price.text;
export const PRICE_UNDONE_NOTE = "I2Q's price counts again.";

/** What a refused price approval reads as (the server answers 403 without a sentence). */
export const APPROVAL_FORBIDDEN_NOTE = "Your role does not allow this.";

/** The toast words of a status a person set. */
const STATUS_TOAST: Record<CaseWork["status"], string> = {
  done: SAVE_TOASTS.statusDone,
  not_for_us: SAVE_TOASTS.statusNotForUs,
  waiting_customer: SAVE_TOASTS.statusWaiting,
  open: SAVE_TOASTS.statusOpen,
};

function sameUser(a: string | null | undefined, b: string | null | undefined): boolean {
  return Boolean(a && b && a.trim().toLowerCase() === b.trim().toLowerCase());
}

/** A toast text with "The case is yours now." after it, when this save took the case. */
export function withTaken(text: string | undefined, taken: boolean): string | undefined {
  if (!taken) {
    return text;
  }
  return [text?.trim(), SAVE_TOASTS.taken].filter(Boolean).join(" ");
}

/**
 * True while a colleague has the case. The page then offers no action that writes many
 * lines in one click ("Set quantity for all lines", "Confirm n items"): "Take over" comes first
 * (review of the ease round, 13 Oct 2026).
 */
export function colleagueHasCase(writes: Pick<CaseWrites, "mine" | "unassigned">): boolean {
  return !writes.mine && !writes.unassigned;
}

/** One write of the "Undo" of a bulk quantity. */
export type QuantityUndoStep =
  | { kind: "restore"; line_keys: string[] }
  | { kind: "set"; line_keys: string[]; qty: number; unit?: string };

/**
 * What "Undo" of a bulk quantity sends, so every line reads as before the bulk write.
 * First the reviewed quantities of the changed lines are taken off (what I2Q read counts
 * again). Then each quantity a person had set before is set again, one write per value.
 * `before` holds the lines as they were when the bulk write was sent (review of the ease
 * round, 13 Oct 2026: a person's 7 box was gone after bulk and "Undo").
 */
export function quantityUndoSteps(
  before: readonly Pick<QuoteLine, "lineKey" | "override">[],
  changed: readonly string[],
): QuantityUndoStep[] {
  const keys = new Set(changed);
  if (keys.size === 0) {
    return [];
  }
  const steps: QuantityUndoStep[] = [{ kind: "restore", line_keys: [...changed] }];
  const sets = new Map<string, Extract<QuantityUndoStep, { kind: "set" }>>();
  for (const line of before) {
    const reviewed = line.override?.kind === "edit" ? line.override : null;
    const qty = typeof reviewed?.qty === "number" ? reviewed.qty : null;
    if (!line.lineKey || !keys.has(line.lineKey) || qty === null) {
      continue;
    }
    const unit = reviewed?.unit?.trim() || undefined;
    const id = `${qty}|${unit ?? ""}`;
    const step = sets.get(id) ?? { kind: "set", line_keys: [], qty, ...(unit ? { unit } : {}) };
    step.line_keys.push(line.lineKey);
    sets.set(id, step);
  }
  return [...steps, ...sets.values()];
}

export interface CaseWrites {
  /** True while any write of the case runs. */
  busy: boolean;
  /** The refused value or conflict of the last write, or null. */
  error: string | null;
  /** True when the case is assigned to the viewer. */
  mine: boolean;
  /** True when nobody has the case. */
  unassigned: boolean;
  /** The key of the line whose decision is on its way, so only that row shows it; else null. */
  decidingLineKey: string | null;
  take: () => void;
  takeOver: () => void;
  release: () => void;
  /**
   * Mark done, not for us, waiting for customer, or reopen; `then` runs after a success.
   * `note` is the status note ("Not an inquiry" for the close offer, hunt round 2).
   */
  setStatus: (status: CaseWork["status"], then?: () => void, note?: string) => void;
  /**
   * Confirm an account (null clears the choice). When the account is the one the deeper
   * search proposes, the request names that proposal, so the history says the answer came
   * from a proposal. `quiet` leaves the toast to the caller (a replaced customer has its own).
   */
  confirmCustomer: (
    account: string | null,
    then?: () => void,
    options?: { quiet?: boolean },
  ) => void;
  /** "Not this one": reject a proposal of the deeper search; takes the case first when unassigned. */
  rejectProposal: (proposalId: string) => void;
  /** "Look again" for the case, or for one line with its key; takes the case first when unassigned. */
  lookAgain: (lineKey?: string) => void;
  /** Confirm every matched line nobody decided. */
  acceptMatched: () => void;
  /** "Confirm n items": confirm these suggested lines in one write. Undo stays per line. */
  confirmSuggested: (lineKeys: string[]) => void;
  /** Confirm, pick, no item or undo on one line. */
  decideLine: (lineIndex: number, lineKey: string, request: LineDecisionRequestBody) => void;
  /** The toast after an item was picked in the item search, with its "Undo". */
  itemPicked: (lineIndex: number, lineKey: string, item: string) => void;
  /**
   * The quantity of one line, typed in its cell: the cell shows it at once, `then` runs
   * after the save (the cell shows its saved mark), a failed save says so in a toast.
   */
  setQuantity: (
    lineIndex: number,
    lineKey: string,
    qty: number,
    unit?: string,
    then?: () => void,
  ) => void;
  /** One quantity for several lines in one write, with "Undo" in the toast. */
  setQuantities: (lineKeys: string[], qty: number, unit?: string, then?: () => void) => void;
  /** Take the reviewed quantities of these lines off: what I2Q read counts again. */
  restoreQuantities: (lineKeys: string[]) => void;
  /**
   * Set or change the price of one line (an "edit" override with the price fields only,
   * so a reviewed quantity stays).
   */
  setPrice: (
    lineIndex: number,
    lineKey: string,
    request: LinePriceRequestBody,
    then?: () => void,
  ) => void;
  /** Take a person's price off one line: I2Q's price counts again. */
  undoPrice: (lineIndex: number, lineKey: string, then?: () => void) => void;
  /** Approve the held price of one line, or take the approval back. One click, one write. */
  approvePrice: (lineIndex: number, approve: boolean) => void;
  /** Ask for a reply draft; takes the case first when unassigned. */
  prepareReply: (again: boolean) => void;
  /** Take the case before a write that does not take it itself; resolves when assigned. */
  ensureTaken: () => Promise<boolean>;
  /** The name of the last confirmed account, for the "being matched" sentence. */
  confirmedAccount: string | null;
}

export function useCaseWrites(detail: QuoteCaseDetail): CaseWrites {
  const { data: me } = useMe();
  const toast = useToastManager();
  const assign = useCaseAssign(detail.id);
  const status = useCaseStatus(detail.id);
  const customer = useCaseCustomer(detail.id);
  const accept = useAcceptMatched(detail.id);
  const decide = useLineDecision(detail.id);
  const override = useLineOverride(detail.id);
  const quantities = useLineQuantities(detail.id);
  const approval = useLinePriceApproval(detail.id);
  const prepare = useCreateAnswerDraft(detail.id);
  const reject = useRejectProposal(detail.id);
  const look = useLookAgain(detail.id);
  const [confirmedAccount, setConfirmedAccount] = useState<string | null>(null);

  const assignee = detail.work?.assignee ?? null;
  const mine = sameUser(assignee, me?.email);
  const open = (detail.workStatus ?? "open") === "open";
  const unassigned = !assignee && open;

  const ensureTaken = async (): Promise<boolean> => {
    if (!unassigned) {
      return true;
    }
    try {
      logger.debug("[case] take before the first write", { case: detail.id });
      await assign.mutateAsync({ action: "take" });
      return true;
    } catch (error) {
      logger.warn("[case] take before the write failed", error);
      return false;
    }
  };

  /**
   * The toast of a save. The first save on a case nobody has also says whose it is now;
   * `takes: false` for a write that never takes the case (a price approval).
   */
  const saved = (
    title: string,
    description?: string,
    action?: { label: string; run: () => void },
    takes = true,
  ) =>
    toast.add({
      title,
      description: withTaken(description, takes && unassigned),
      type: "success",
      ...(action ? { actionProps: { children: action.label, onClick: action.run } } : {}),
    });
  /** "Not saved", with the sentence of the refusal. A conflict has its own toast already. */
  const failed = (error: unknown, target: "line" | "case" | "customer" | "quantity" = "case") => {
    const sentence = workFailureText(error, target);
    if (sentence) {
      toast.add({ title: SAVE_TOASTS.failed.title, description: sentence, type: "error" });
    }
  };
  const row = (lineIndex: number) => rowNumberOf(detail, lineIndex);

  const busy =
    assign.isPending ||
    status.isPending ||
    customer.isPending ||
    accept.isPending ||
    decide.isPending ||
    override.isPending ||
    quantities.isPending ||
    approval.isPending ||
    prepare.isPending ||
    reject.isPending ||
    look.isPending;
  const error =
    workFailureText(assign.error) ??
    workFailureText(status.error) ??
    workFailureText(customer.error, "customer") ??
    workFailureText(accept.error, "line") ??
    workFailureText(decide.error, "line") ??
    workFailureText(override.error, "line") ??
    workFailureText(quantities.error, "quantity") ??
    // 422 of "Look again" carries the server's sentence ("Clear the decision first.").
    workFailureText(look.error) ??
    ((reject.error as { status?: number } | null)?.status === 404
      ? PROPOSAL_GONE_NOTE
      : workFailureText(reject.error));

  const clearDecision = (lineIndex: number, lineKey: string) => {
    logger.debug("[case] line decision", { case: detail.id, lineIndex, kind: "clear" });
    decide.mutate(
      { lineIndex, line_key: lineKey, kind: "clear" },
      { onError: (cause) => failed(cause, "line") },
    );
  };
  const restoreQuantities = (lineKeys: string[]) => {
    if (lineKeys.length === 0) {
      return;
    }
    logger.debug("[case] quantities restore", { case: detail.id, lines: lineKeys.length });
    quantities.mutate(
      { kind: "restore", line_keys: lineKeys },
      { onError: (cause) => failed(cause, "quantity") },
    );
  };
  /**
   * "Undo" of a bulk quantity: the steps of `quantityUndoSteps`, one after the other (the
   * writes of a case run in a row). Each step has its own answer (`mutateAsync`); the
   * first failure says "Not saved" and the steps after it are not sent.
   */
  const undoQuantities = async (steps: QuantityUndoStep[]) => {
    logger.debug("[case] quantities undo", { case: detail.id, steps: steps.length });
    try {
      for (const step of steps) {
        await quantities.mutateAsync(step);
      }
    } catch (cause) {
      failed(cause, "quantity");
    }
  };
  const approvePrice = (lineIndex: number, approve: boolean) => {
    logger.debug("[case] price approval", { case: detail.id, lineIndex, approve });
    approval.mutate(
      { lineIndex, approve },
      {
        // An approval never takes the case (the server keeps the assignee), so its toast
        // never says "The case is yours now.".
        onSuccess: () =>
          approve
            ? saved(
                SAVE_TOASTS.priceApproved.title,
                SAVE_TOASTS.priceApproved.text(row(lineIndex)),
                {
                  label: ACTION_LABELS.takeBackApproval,
                  run: () => approvePrice(lineIndex, false),
                },
                false,
              )
            : saved(
                SAVE_TOASTS.approvalTakenBack.title,
                SAVE_TOASTS.approvalTakenBack.text(row(lineIndex)),
                undefined,
                false,
              ),
        onError: (cause) => {
          const failure = approvalFailure(cause);
          if (failure.kind === "conflict") {
            return;
          }
          toast.add({
            title: SAVE_TOASTS.failed.title,
            description: failure.kind === "forbidden" ? APPROVAL_FORBIDDEN_NOTE : failure.message,
            type: "error",
          });
        },
      },
    );
  };
  const undoPrice = (lineIndex: number, lineKey: string, then?: () => void) => {
    logger.debug("[case] line price undo", { case: detail.id, lineIndex });
    // `unit_price: null` explicitly (JSON keeps null, drops undefined): that is the undo.
    override.mutate(
      { lineIndex, line_key: lineKey, kind: "edit", unit_price: null },
      {
        onSuccess: () => {
          saved("Price taken off", PRICE_UNDONE_NOTE);
          then?.();
        },
      },
    );
  };
  const setStatus = (next: CaseWork["status"], then?: () => void, note?: string) => {
    logger.debug("[case] set status", { case: detail.id, status: next, noted: Boolean(note) });
    status.mutate(note ? { status: next, note } : { status: next }, {
      onSuccess: () => {
        // A status that closes the case offers the way back.
        saved(
          STATUS_TOAST[next],
          undefined,
          next === "open"
            ? undefined
            : { label: ACTION_LABELS.reopen, run: () => setStatus("open") },
        );
        then?.();
      },
      onError: (cause) => failed(cause),
    });
  };
  const confirmCustomer: CaseWrites["confirmCustomer"] = (account, then, options) => {
    const proposalId = customerProposalIdFor(detail.investigation, account);
    logger.debug("[case] customer", { case: detail.id, account, proposal: proposalId });
    customer.mutate(proposalId ? { account, proposal_id: proposalId } : { account }, {
      onSuccess: () => {
        setConfirmedAccount(account);
        if (account && !options?.quiet) {
          saved(SAVE_TOASTS.customer.title, undefined, {
            label: ACTION_LABELS.undo,
            run: () => confirmCustomer(null, undefined, { quiet: true }),
          });
        }
        then?.();
      },
    });
  };

  return {
    busy,
    error,
    mine,
    unassigned,
    confirmedAccount,
    ensureTaken,
    decidingLineKey: decide.isPending ? (decide.variables?.line_key ?? null) : null,
    take: () => {
      logger.debug("[case] assign", { case: detail.id, action: "take" });
      assign.mutate({ action: "take" });
    },
    takeOver: () => {
      logger.debug("[case] assign", { case: detail.id, action: "take_over" });
      assign.mutate({ action: "take_over" });
    },
    release: () => {
      logger.debug("[case] assign", { case: detail.id, action: "release" });
      assign.mutate({ action: "release" });
    },
    setStatus,
    confirmCustomer,
    rejectProposal: (proposalId) => {
      void ensureTaken().then((taken) => {
        if (!taken) {
          return;
        }
        logger.debug("[case] reject proposal", { case: detail.id, proposal: proposalId });
        reject.mutate(
          { proposal_id: proposalId },
          {
            onSuccess: () =>
              toast.add({
                title: INVESTIGATION_WORDS.rejectedToast,
                description: INVESTIGATION_WORDS.rejectedNote,
                type: "success",
              }),
          },
        );
      });
    },
    lookAgain: (lineKey) => {
      void ensureTaken().then((taken) => {
        if (!taken) {
          return;
        }
        logger.debug("[case] look again", { case: detail.id, line: lineKey ?? null });
        look.mutate(lineKey ? { line_key: lineKey } : {}, {
          onSuccess: () =>
            toast.add({
              title: INVESTIGATION_WORDS.lookingAgain,
              description: INVESTIGATION_WORDS.lookAgainAsked,
              type: "success",
            }),
        });
      });
    },
    acceptMatched: () => {
      logger.debug("[case] accept matched", { case: detail.id });
      accept.mutate(null, {
        onSuccess: (result) =>
          saved("Matched items accepted", acceptedText(result?.accepted?.length ?? 0)),
        onError: (cause) => failed(cause, "line"),
      });
    },
    confirmSuggested: (lineKeys) => {
      if (lineKeys.length === 0) {
        return;
      }
      logger.debug("[case] confirm suggested", { case: detail.id, lines: lineKeys.length });
      accept.mutate(
        { lineKeys, includeSuggested: true },
        {
          onSuccess: (result) =>
            saved(
              SAVE_TOASTS.linesConfirmed.title,
              SAVE_TOASTS.linesConfirmed.text(result?.accepted?.length ?? lineKeys.length),
            ),
          onError: (cause) => failed(cause, "line"),
        },
      );
    },
    decideLine: (lineIndex, lineKey, request) => {
      // A confirm or a pick of the item the deeper search proposes for this line names
      // that proposal, so the history says the answer came from a proposal.
      const line = detail.lines.find((candidate) => candidate.lineKey === lineKey);
      const proposalId = line ? lineProposalIdFor(detail.investigation, line, request) : undefined;
      logger.debug("[case] line decision", {
        case: detail.id,
        lineIndex,
        kind: request.kind,
        proposal: proposalId,
      });
      const undo = { label: ACTION_LABELS.undo, run: () => clearDecision(lineIndex, lineKey) };
      // Each row gets its own answer (`mutateAsync`), also when the next row is confirmed
      // before this one is back.
      decide
        .mutateAsync({
          lineIndex,
          line_key: lineKey,
          ...request,
          ...(proposalId ? { proposal_id: proposalId } : {}),
        })
        .then(() => {
          if (request.kind === "no_item") {
            // The row moves into the folded "Nothing to quote" group: the toast says
            // so and carries the Undo (tester round 5, 12 Oct 2026).
            saved(NO_ITEM_TOAST.title, NO_ITEM_TOAST.description, undo);
          } else if (request.kind === "confirm") {
            saved(
              SAVE_TOASTS.lineConfirmed.title,
              SAVE_TOASTS.lineConfirmed.text(row(lineIndex), line?.matchedItemId ?? ""),
              undo,
            );
          } else if (request.kind === "item") {
            saved(
              SAVE_TOASTS.itemPicked.title,
              SAVE_TOASTS.itemPicked.text(row(lineIndex), request.item ?? ""),
              undo,
            );
          }
        })
        .catch((cause: unknown) => failed(cause, "line"));
    },
    itemPicked: (lineIndex, lineKey, item) =>
      saved(SAVE_TOASTS.itemPicked.title, SAVE_TOASTS.itemPicked.text(row(lineIndex), item), {
        label: ACTION_LABELS.undo,
        run: () => clearDecision(lineIndex, lineKey),
      }),
    setQuantity: (lineIndex, lineKey, qty, unit, then) => {
      logger.debug("[case] line quantity", { case: detail.id, lineIndex });
      // The quantity route keeps a reviewed unit and a price a person set. Each cell gets
      // its own answer (`mutateAsync`), also when the next cell saves before this one is back.
      quantities
        .mutateAsync({ kind: "set", line_keys: [lineKey], qty, ...(unit ? { unit } : {}) })
        .then(() => then?.())
        .catch((cause: unknown) => failed(cause, "quantity"));
    },
    setQuantities: (lineKeys, qty, unit, then) => {
      if (lineKeys.length === 0) {
        return;
      }
      logger.debug("[case] quantities", { case: detail.id, lines: lineKeys.length });
      // The lines as they are now: "Undo" puts back a quantity a person had set before.
      const before = detail.lines.map((line) => ({
        lineKey: line.lineKey,
        override: line.override,
      }));
      quantities.mutate(
        { kind: "set", line_keys: lineKeys, qty, ...(unit ? { unit } : {}) },
        {
          onSuccess: (result) => {
            const changed = result?.changed ?? lineKeys;
            if (changed.length === 0) {
              saved(SAVE_TOASTS.noChange.title, SAVE_TOASTS.noChange.text);
            } else {
              saved(SAVE_TOASTS.quantityAll.title(changed.length), SAVE_TOASTS.quantityAll.text, {
                label: ACTION_LABELS.undo,
                run: () => void undoQuantities(quantityUndoSteps(before, changed)),
              });
            }
            then?.();
          },
          onError: (cause) => failed(cause, "quantity"),
        },
      );
    },
    restoreQuantities,
    setPrice: (lineIndex, lineKey, request, then) => {
      logger.debug("[case] line price", {
        case: detail.id,
        lineIndex,
        currency: request.currency,
      });
      // No qty or unit keys: the backend then keeps the reviewed quantity and replaces
      // only the price group (its rule keys on which fields were sent).
      override.mutate(
        { lineIndex, line_key: lineKey, kind: "edit", ...request },
        {
          onSuccess: () => {
            saved(SAVE_TOASTS.price.title, SAVE_TOASTS.price.text, {
              label: ACTION_LABELS.undo,
              run: () => undoPrice(lineIndex, lineKey),
            });
            then?.();
          },
        },
      );
    },
    undoPrice,
    approvePrice,
    prepareReply: (again) => {
      void ensureTaken().then((taken) => {
        if (!taken) {
          return;
        }
        logger.debug("[case] prepare reply", { case: detail.id, again });
        prepare.mutate(again);
      });
    },
  };
}
